import type { WorkflowRun, WorkflowRunStep } from '../../api/workflowRuns'

export type WorkflowRunArtifactBlock =
  | { kind: 'text'; title: string; text: string }
  | { kind: 'images'; title: string; urls: string[] }

function looksLikeHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim())
}

function collectUrls(value: unknown, urls: Set<string>) {
  if (typeof value === 'string') {
    if (looksLikeHttpUrl(value)) urls.add(value.trim())
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) collectUrls(item, urls)
    return
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      collectUrls(item, urls)
    }
  }
}

function pushUniqueText(texts: string[], seen: Set<string>, raw: unknown) {
  if (typeof raw !== 'string') return
  const text = raw.trim()
  if (!text || seen.has(text) || looksLikeHttpUrl(text)) return
  seen.add(text)
  texts.push(text)
}

function pushStructuredText(texts: string[], seen: Set<string>, raw: unknown) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
  try {
    const text = JSON.stringify(raw, null, 2)
    if (!text || seen.has(text)) return
    seen.add(text)
    texts.push(text)
  } catch {
    /* ignore unserializable values */
  }
}

export function buildWorkflowStepArtifactBlocks(step: WorkflowRunStep): WorkflowRunArtifactBlock[] {
  const blocks: WorkflowRunArtifactBlock[] = []
  const output = step.output ?? {}
  const texts: string[] = []
  const seen = new Set<string>()

  const preferredTextKeys = [
    'llmOutput',
    'prompt',
    'text',
    'comicProjectPlan',
    'imageRequest',
    'imageResult',
    'storyboardText',
    'summary',
    'lastConditionActual',
  ] as const
  for (const key of preferredTextKeys) {
    pushUniqueText(texts, seen, output[key])
  }
  pushStructuredText(texts, seen, output.comicProjectPlan)
  pushStructuredText(texts, seen, output.imageRequest)
  pushStructuredText(texts, seen, output.imageResult)

  if (step.nodeType === 'loop.until') {
    const iteration = step.output?.lastLoopIteration
    const passed = step.output?.lastLoopPassed
    if (typeof iteration === 'number' && iteration > 0) {
      const exitText = typeof passed === 'boolean' ? (passed ? '满足退出条件' : '继续循环体') : '轮次已更新'
      texts.unshift(`循环轮次：第 ${iteration} 轮 · ${exitText}`)
    }
  }

  if (step.nodeType === 'condition.check') {
    const passed = output.lastConditionPassed
    if (typeof passed === 'boolean') {
      texts.unshift(`条件结果：${passed ? '满足' : '不满足'}`)
    }
  }

  if (texts.length > 0) {
    blocks.push({ kind: 'text', title: '文本产物', text: texts.join('\n\n') })
  }

  const imageUrls = new Set<string>()
  collectUrls(output.images, imageUrls)
  collectUrls(output, imageUrls)
  if (imageUrls.size > 0) {
    blocks.push({ kind: 'images', title: '图片产物', urls: [...imageUrls] })
  }

  if (step.externalRefId) {
    blocks.push({
      kind: 'text',
      title: '外部引用',
      text: `${step.externalRefType || 'ref'}: ${step.externalRefId}`,
    })
  }

  if (blocks.length === 0 && step.errorMessage) {
    blocks.push({ kind: 'text', title: '错误', text: step.errorMessage })
  }

  return blocks
}

export function selectDefaultWorkflowRunStep(run: WorkflowRun | null): WorkflowRunStep | null {
  if (!run || run.steps.length === 0) return null
  const running = run.steps.find((step) => step.status === 'running')
  if (running) return running
  const failed = [...run.steps].reverse().find((step) => step.status === 'failed')
  if (failed) return failed
  const succeeded = [...run.steps].reverse().find((step) => step.status === 'succeeded' && Object.keys(step.output).length > 0)
  if (succeeded) return succeeded
  return run.steps[0]
}

export function workflowRunProgressPercent(run: WorkflowRun): number {
  if (run.steps.length === 0) return 0
  const done = run.steps.filter((step) =>
    step.status === 'succeeded' || step.status === 'failed' || step.status === 'cancelled',
  ).length
  return Math.round((done / run.steps.length) * 100)
}
